/**
 * LEAD360-DEDUP-V1 (V1-P3) -- THE canonical full-page Lead 360 detail
 * (`/dashboard/leads/:safeLeadId`). Every list surface drills here or into a
 * quick-view of the same component; the owner detail route redirects into it.
 *
 * Composes the ONE canonical `LeadDetailContent` (Lead360CanonicalView: header +
 * NEXT-card contact workflow + journey + timeline + linkage + honesty) with the
 * workspace back-link. Data comes from the role-scoped workspace endpoint -- the
 * backend enforces role + org + clinic scope AND the role-aware section
 * projection (restricted-section field VALUES are absent, not null), so a lead
 * outside the caller's verified scope is simply never in the response and the
 * honest "not found in the current clinic scope" state renders. The NEXT-card
 * contact workflow posts to the ONE audited contact-workflow server action
 * (LEAD-CONTACT-FLOW-V1), which enforces its own action fence + org + clinic
 * scope server-side.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  assignLead,
  bookAppointment,
  setLeadBookingClinic,
  confirmAppointment,
  cancelAppointment,
  claimLead,
  fetchAssignableStaff,
  fetchLeadDispositionOptions,
  fetchLeadTimeline,
  fetchLeadsWorkspace,
  markAppointmentArrived,
  markAppointmentCheckedIn,
  markAppointmentFulfilled,
  markAppointmentNoShow,
  markAppointmentNotConverted,
  releaseLead,
  rescheduleAppointment,
  saveLeadDisposition,
  saveLeadNote,
  type LeadDispositionOptionsResponse,
  type LeadTimelineResponse,
} from '@/dashboard/api';
import LeadNoteComposer from '@/dashboard/leads/LeadNoteComposer';
import { readConversationFromTimeline } from '@/dashboard/leads/LeadConversationTranscript';
import type { LeadRecordTimelineState } from '@/dashboard/leads/Lead360CanonicalView';
import LeadDispositionPanel from '@/dashboard/leads/LeadDispositionPanel';
import LastContactAttemptSummary from '@/dashboard/leads/LastContactAttemptSummary';
import { canAssign } from '@/dashboard/leads/LeadOwnerControls';
import { canActOnLeadsWorkspace } from '@/dashboard/leads/lead-list-contract';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import { useDashboardMeContext } from '@/dashboard/me-context';
// THE ONE origin grammar (founder ruling 2026-08-08: CAMPAIGN -> SOURCE ->
// CHANNEL) -- reused so the booking header can never disagree with the
// record masthead's chip. Never a second attribution vocabulary.
import { resolveOriginChipLabel } from '@/dashboard/owner/Lead360Panel';
import LeadPatientCrossmatchNotice from '@/dashboard/patients/LeadPatientCrossmatchNotice';
import { LeadDetailContent } from '@/dashboard/leads/Lead360CanonicalView';
import { dispositionChainsToBooking } from '@/dashboard/leads/lead-record-experience';
import LeadOwnerControls from '@/dashboard/leads/LeadOwnerControls';
import AppointmentWorkbench, {
  canUseAppointmentWorkbench,
  isBookConfirmOnlyRole,
} from '@/dashboard/owner/AppointmentWorkbench';
import { resolveCancelReasonCodes } from '@/dashboard/appointments/cancel-reasons';
import { formatLeadRef } from '@/dashboard/owner/leads-object-view';
import type { Lead360Event, Lead360Input } from '@/dashboard/owner/lead-360-view';
import { WORKSPACE_LEADS_LIMIT } from '@/dashboard/leads/LeadsWorkspacePage';
import { LoadingState } from '@/dashboard/ui';

const BACK_TO_WORKSPACE = '/dashboard/leads';

export default function LeadsWorkspaceDetailPage(): JSX.Element {
  const params = useParams();
  const safeLeadId = params.safeLeadId ?? '';
  const [searchParams] = useSearchParams();
  const clinicIdParam = searchParams.get('clinicId');
  const meCtx = useDashboardMeContext();
  // S2-P2C: workbench visibility (role + /me flag). Fail-closed when absent.
  const workbenchEnabled = canUseAppointmentWorkbench(
    meCtx?.me?.role,
    meCtx?.me?.crmAppointmentWorkbenchEnabled,
  );
  // LEAD-ASSIGNMENT-OWNERSHIP-V1: the owner controls are live ONLY when the
  // server-computed /me flag is on. Fail-closed when absent. Each record is
  // ADDITIONALLY gated on `record.ownerAssignment` presence below, so a
  // record without the overlay renders the (byte-identical) flag-off body.
  const me = meCtx?.me ?? null;
  const assignmentEnabled = me?.leadAssignmentEnabled === true;
  // MANAGER-LEAD-READ-ACCESS-CORRECTIVE-V1 (SEE-NOT-DO): the disposition
  // panel is gated on the workspace ACTION audience, not on reaching this
  // page. Since the 2026-07-28 ruling, gm_agm / regional_head reach this
  // canonical record (READ), while postLeadDisposition refuses their save
  // (requireLeadsWorkspaceActionScope). Drawing the panel for them would be
  // a door that cannot open (LES-016), so it is structurally absent. Every
  // OTHER control on this page keeps its OWN pre-existing gate below and is
  // untouched by this corrective -- ROUTE FENCE IS NOT FIELD FENCE.
  const canDisposition = canActOnLeadsWorkspace(me?.role);

  const [records, setRecords] = useState<Lead360Input[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bumped on a successful appointment mutation to re-fetch the lead.
  const [reloadKey, setReloadKey] = useState(0);
  // LEAD-TRUTH-AND-DEPENDENT-STATE-CORRECTIVE-V1 (Fix 2 -- LIVE
  // DERIVATIONS): a mutation refetch re-derives IN PLACE. The skeleton
  // renders only while no records exist for the clinic in view (first load /
  // clinic switch / error recovery); a reloadKey refetch keeps the current
  // record mounted -- header, band, path bar, last-contact, and
  // upcoming/overdue re-derive from the fresh read without a full-tree
  // unmount, so save feedback and scroll position survive.
  const [refreshing, setRefreshing] = useState(false);
  const loadedClinicRef = useRef<string | null | undefined>(undefined);
  // RA-2 chain, re-doored by LEAD-CONTACT-FLOW-V1: a disposition save whose
  // next action is book_appointment bumps this signal; the record then
  // opens its BOOKING MODAL (the one booking door). 0 = no pending chain.
  const [bookingChainSignal, setBookingChainSignal] = useState(0);
  // CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1: the config-backed panel
  // vocabularies (fail-safe seeded server-side). A read fault leaves them
  // null -> the panel + no-default reveal simply do not render (the byte-
  // identical fallback), never a broken surface.
  const [dispositionOptions, setDispositionOptions] =
    useState<LeadDispositionOptionsResponse | null>(null);
  // OMNICHANNEL-TIMELINE-AND-LEAD-360-V1: the WINDOWED timeline wire state.
  // Fetched per lead (offset 0, server default limit); "Load older history"
  // appends the next window; a composer save or record mutation refetches
  // the loaded window IN PLACE (the Fix-2 in-place idiom -- no unmount).
  const [timelineData, setTimelineData] = useState<LeadTimelineResponse | null>(null);
  const [timelineStatus, setTimelineStatus] =
    useState<'loading' | 'error' | 'ready'>('loading');
  const [timelineLoadingMore, setTimelineLoadingMore] = useState(false);
  const [timelineReloadKey, setTimelineReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // In-place refetch (Fix 2): only a clinic switch / first load drops to
    // the skeleton; a same-clinic reloadKey bump re-derives over the
    // mounted record (aria-busy via `refreshing`).
    const inPlace = loadedClinicRef.current === clinicIdParam;
    if (inPlace) setRefreshing(true);
    setError(null);
    fetchLeadsWorkspace(clinicIdParam, WORKSPACE_LEADS_LIMIT)
      .then((data) => {
        if (!cancelled) {
          loadedClinicRef.current = clinicIdParam;
          setRecords([...data.records]);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setRecords(null);
          loadedClinicRef.current = undefined;
        }
      })
      .finally(() => {
        if (!cancelled) setRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clinicIdParam, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    fetchLeadDispositionOptions()
      .then((data) => {
        if (!cancelled) setDispositionOptions(data);
      })
      .catch(() => {
        // Fail-soft: the panel is an enhancement, never a page blocker.
        if (!cancelled) setDispositionOptions(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The windowed timeline read (newest-first; totals disclosed). A record
  // mutation (reloadKey) or a composer save (timelineReloadKey) refetches
  // the CURRENTLY LOADED window in place so the new touch appears at the
  // top without losing the operator's scroll depth.
  // WIRE-SHAPE HONESTY: a malformed/empty timeline response (a proxy fault,
  // a mock, an old server) must degrade to the honest error/empty states --
  // never a component crash. Normalize ONCE here; every consumer reads the
  // normalized shape.
  const timelineEntries: readonly Lead360Event[] = Array.isArray(
    timelineData?.entries,
  )
    ? timelineData.entries
    : [];
  const timelineTotalCount =
    typeof timelineData?.totalCount === 'number'
      ? timelineData.totalCount
      : timelineEntries.length;

  useEffect(() => {
    if (safeLeadId === '') return;
    let cancelled = false;
    const limit = Math.min(Math.max(50, timelineEntries.length + 1), 200);
    setTimelineStatus((prev) => (timelineData === null ? 'loading' : prev));
    fetchLeadTimeline(safeLeadId, { offset: 0, limit })
      .then((data) => {
        if (!cancelled) {
          setTimelineData(data);
          setTimelineStatus('ready');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTimelineStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the loaded
    // count is deliberately read, not depended on (a depth-preserving
    // refetch keyed only on the lead + the reload signals).
  }, [safeLeadId, reloadKey, timelineReloadKey]);

  const loadOlderTimeline = (): void => {
    if (timelineData === null || timelineLoadingMore) return;
    setTimelineLoadingMore(true);
    fetchLeadTimeline(safeLeadId, {
      offset: timelineEntries.length,
      limit: 50,
    })
      .then((page) => {
        setTimelineData((prev) => {
          const prevEntries = Array.isArray(prev?.entries) ? prev.entries : [];
          const pageEntries = Array.isArray(page?.entries) ? page.entries : [];
          return prev === null
            ? page
            : { ...page, entries: [...prevEntries, ...pageEntries], offset: 0 };
        });
      })
      .catch(() => {
        // The loaded window stays; the button simply remains available.
      })
      .finally(() => setTimelineLoadingMore(false));
  };

  const timelineState: LeadRecordTimelineState = {
    entries: timelineEntries,
    status: timelineStatus,
    totalCount: timelineTotalCount,
    hasMore: timelineData !== null && timelineEntries.length < timelineTotalCount,
    loadingMore: timelineLoadingMore,
    onLoadMore: loadOlderTimeline,
    onRetry: (): void => setTimelineReloadKey((k) => k + 1),
    eventStreamStatus: timelineData?.eventStreamStatus ?? 'not_wired',
    // OMNICHANNEL-CONVERSATION-SPINE-V1: the conversation leg off the SAME
    // timeline read (no second fetch, no second endpoint). null until the
    // first read lands or when the wire carried no leg -> the honest seam.
    conversation: readConversationFromTimeline(timelineData),
    notesStreamStatus:
      timelineData?.notesStreamStatus === 'available' ? 'available' : 'unavailable',
  };

  if (error !== null) {
    return (
      <section className="lead-detail">
        <div className="page-header">
          <h1>{formatLeadRef(safeLeadId)}</h1>
        </div>
        <div className="mb-4">
          <DashboardLoadError message={error} />
        </div>
      </section>
    );
  }

  if (records === null) {
    return (
      <section className="lead-detail">
        <div className="page-header">
          <h1>{formatLeadRef(safeLeadId)}</h1>
        </div>
        {/* V7-P4A: the bare loading div becomes the canonical LoadingState
            (role=status + printed label + skeleton strips). */}
        <LoadingState
          label="Loading the lead record..."
          lines={3}
          testId="lead-detail-loading"
        />
      </section>
    );
  }

  return (
    <div
      aria-busy={refreshing || undefined}
      data-testid="lead-detail-live"
      data-refreshing={refreshing ? 'yes' : 'no'}
    >
      {/*
        FRONT-DESK-POSSIBLE-MATCH-SURFACE-01: advisory "possible existing
        patient" notice for the lead in view. Self-gated by the server
        `me.patientMasterEnabled` signal (renders nothing AND never fetches
        when the flag is off). Display-only — never links / merges / creates.
      */}
      <LeadPatientCrossmatchNotice safeLeadId={safeLeadId} reloadToken={reloadKey} />
      <LeadDetailContent
        records={records}
        safeLeadId={safeLeadId}
        backHref={BACK_TO_WORKSPACE}
        polished
        canOpenPatient={meCtx?.me?.patientMasterEnabled === true}
        /* CALL-CENTER-LEAD-360-SALESFORCE-EXPERIENCE-V1 (Mission 2): the
           header action bar's Reassign anchor renders ONLY for callers
           holding the assign-others door (the EXISTING canAssign
           authority mirror -- manager tiers; agents never see it). */
        canReassign={assignmentEnabled && canAssign(me)}
        /* LEAD-RECORD-POLISH-BATCH-V1 (CC-11): the org's CONFIGURED
           lead-routing mode, straight off /me (server truth). The unclaimed
           seat explains itself FROM this real state -- with the field absent
           (a caller outside the assignment audience, an older /me, a read
           fault) the seat states the queue and claims no reason. */
        assignmentMode={me?.leadAssignmentMode ?? null}
        bookingChainSignal={bookingChainSignal}
        renderLastContactAttempt={(record): ReactNode => (
          <LastContactAttemptSummary record={record} />
        )}
        timeline={timelineState}
        /* The composer's ADD A NOTE: the SAME action audience as the
           disposition panel (SEE-NOT-DO -- gm_agm/regional_head read the
           record and get NO composer; the server refuses their POST
           independently). The seam state rides the timeline read. */
        renderNoteComposer={
          canDisposition
            ? (record): ReactNode => (
                <LeadNoteComposer
                  notesStoreAvailable={
                    timelineState.notesStreamStatus === 'available'
                  }
                  save={(body) => saveLeadNote(String(record.safeLeadId), body)}
                  onSaved={(): void => setTimelineReloadKey((k) => k + 1)}
                />
              )
            : undefined
        }
        renderDispositionPanel={
          canDisposition && dispositionOptions !== null
            ? (record, opts): ReactNode => (
                <LeadDispositionPanel
                  vocabulary={dispositionOptions.dispositionOptions}
                  {...(opts?.heading !== undefined
                    ? { heading: opts.heading }
                    : {})}
                  save={(body) =>
                    saveLeadDisposition(String(record.safeLeadId), body)
                  }
                  onSaved={(saved): void => {
                    // RA-2: the save completed first; a chaining next action
                    // (book_appointment) then opens the record's BOOKING
                    // MODAL (the one booking door) once re-derived.
                    if (dispositionChainsToBooking(saved.nextActionId)) {
                      setBookingChainSignal((k) => k + 1);
                    }
                    setReloadKey((k) => k + 1);
                  }}
                />
              )
            : undefined
        }
        renderAppointmentControl={
          workbenchEnabled
            ? // CHANNEL-TO-BOOKED-APPOINTMENT-V1: a WhatsApp / chat enquiry
              // can arrive with NO branch. The record already carries the
              // clinic (null when branchless) and the attribution the
              // masthead chip reads, so the branch step needs no new wire
              // field -- only the four props below.
              (record): ReactNode => (
                <AppointmentWorkbench
                  leadId={Number(record.safeLeadId)}
                  section={record.appointmentSection}
                  bookConfirmOnly={isBookConfirmOnlyRole(meCtx?.me?.role)}
                  rescheduleGranted={meCtx?.me?.appointmentRescheduleGranted === true}
                  allowConfirmation={meCtx?.me?.role !== 'receptionist'}
                  book={bookAppointment}
                  leadClinicId={record.clinicId}
                  clinicOptions={me?.clinicOptions ?? []}
                  originLabel={resolveOriginChipLabel({
                    sourceNormalized: record.sourceNormalized,
                    utmSource: record.utmSource,
                    utmCampaign: record.utmCampaign,
                  })}
                  setBookingClinic={setLeadBookingClinic}
                  confirm={confirmAppointment}
                  reschedule={rescheduleAppointment}
                  cancel={cancelAppointment}
                  cancelReasonCodes={resolveCancelReasonCodes(me)}
                  noShow={markAppointmentNoShow}
                  arrived={markAppointmentArrived}
                  checkedIn={markAppointmentCheckedIn}
                  fulfilled={markAppointmentFulfilled}
                  notConverted={markAppointmentNotConverted}
                  onChanged={(): void => setReloadKey((k) => k + 1)}
                />
              )
            : undefined
        }
        renderOwnerControl={
          assignmentEnabled
            ? (record): ReactNode =>
                record.ownerAssignment !== undefined ? (
                  <LeadOwnerControls
                    leadId={Number(record.safeLeadId)}
                    ownership={record.ownerAssignment}
                    /* LEAD-360-CONFORMANCE-B2 (Ruling 1 -- SAY-ONCE RIDER):
                       the masthead summary strip retired, so the composed
                       rail's Owner section is now the ONE home for the
                       owner row -- showOwnerRow flips back on, and
                       renderChrome turns off because the rail's
                       SectionContainer already draws the counted "Owner"
                       header (avoiding a doubled heading). The quick view
                       (LeadQuickView.tsx) is untouched: default props there
                       stay byte-identical. */
                    showOwnerRow
                    renderChrome={false}
                    me={me}
                    claim={claimLead}
                    assign={assignLead}
                    release={releaseLead}
                    loadAssignableStaff={fetchAssignableStaff}
                    onChanged={(): void => setReloadKey((k) => k + 1)}
                  />
                ) : null
            : undefined
        }
      />
    </div>
  );
}
